import { ChangeEvent, FC, FormEvent, useEffect, useRef, useState } from "react";
import apiClient from "../../utils/apiClient";
import { Box, SelectChangeEvent } from "@mui/material";
import { TabContext, TabPanel } from "@mui/lab";
import { AxiosError } from "axios";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState } from "../../store";
import Provider from "../../enum/Provider.enum";
import AgentLegalForm from "../../enum/AgentLegalForm.enum";
import FilesSection from "../FilesSection";
import RekvizSection from "../RekvizSection";
import { AgentFormState } from "../../types/agentTypes";
import AgentTabs from "./sections/AgentTabs";
import { FormContainer, Section } from "./styles";
import {
  getInvalidFormFieldNames,
  isOgrnipTooLong,
  isValidArmenianText,
  shouldLog,
} from "./helpers";
import {
  CreateAgentFormType,
  MotivationResponseType,
  SelectMotivationType,
  TabsType,
} from "./types";
import { agentInitialStates, initialBaseState } from "./constants";
import ClosedAgentAlert from "./sections/ClosedAgentAlert";
import DefaultTabContent from "./sections/DefaultTabContent";
import { hasOpenAccount } from "../../utils/accountActivation";

const getOgrnipValue = (state: AgentFormState) => {
  return "ogrnip" in state ? state.ogrnip : "";
};

const getSanitizedDraftPayload = (draft: any) => {
  const payload = draft?.payload_json ?? {};
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return initialBaseState;
  }

  const { __draftMeta, ...rest } = payload;
  return {
    ...initialBaseState,
    ...rest,
  };
};

const CreateAgentForm: FC<CreateAgentFormType> = ({
  agent,
  draft,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();

  const isDraftMode = !!draft?.id;
  const isAgentEditMode = !!agent?.id && !isDraftMode;
  const isCreateMode = !agent?.id && !isDraftMode;
  const { provider } = useSelector((state: RootState) => state.app);
  const [formData, setFormData] = useState<AgentFormState>(
    agentInitialStates.base,
  );
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isDeletingDraft, setIsDeletingDraft] = useState(false);

  const [motivations, setMotivations] = useState<SelectMotivationType[]>([]);
  const [motivationsLoading, setMotivationsLoading] = useState<boolean>(false);

  const initialFormDataRef = useRef<AgentFormState>(initialBaseState);
  const formRef = useRef<HTMLFormElement | null>(null);
  const [tab, setTab] = useState<TabsType>("default");

  const [tnClient, setTnClient] = useState<Record<string, any>>();
  const [loadingTn, setLoadingTn] = useState(false);
  const [debouncedTnId, setDebouncedTnId] = useState("");
  const [tnError, setTnError] = useState(false);
  const [isOgrnipOverflow, setIsOgrnipOverflow] = useState(false);

  const [isTechnical, setIsTechnical] = useState(false);
  const techOnly = !!formData.is_technical;
  const requiresContractDate = [Provider.UAE, Provider.TFOS].includes(provider);
  const isFirstNameAmInvalid = !isValidArmenianText(formData.first_name_am);
  const isLastNameAmInvalid = !isValidArmenianText(formData.last_name_am);
  const isPatronymicAmInvalid = !isValidArmenianText(formData.patronymic_am);
  const hasInvalidArmenianFields =
    isFirstNameAmInvalid || isLastNameAmInvalid || isPatronymicAmInvalid;
  const hasInvalidOgrnip =
    isOgrnipOverflow || isOgrnipTooLong(getOgrnipValue(formData));

  useEffect(() => {
    const fetchMotivations = async () => {
      try {
        setMotivationsLoading(true);
        const response: MotivationResponseType = await apiClient.get(
          "/data/GetHandbookMotivations",
        );
        if (response.data.success) {
          setMotivations(response.data.select);
        }
      } catch (error) {
      } finally {
        setMotivationsLoading(false);
      }
    };

    fetchMotivations();
  }, []);

  const isDirty = Object.keys(formData).some((key) => {
    const typedKey = key as keyof AgentFormState;
    return formData[typedKey] !== initialFormDataRef.current[typedKey];
  });
  const canSaveDraft = isDirty;

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: TabsType,
  ) => {
    setTab(newValue);
  };

  const handleFormatChange = (e: SelectChangeEvent) => {
    const newFormat = e.target.value as AgentLegalForm;

    if (newFormat === AgentLegalForm.PERSON) {
      setFormData((prev) => ({
        ...agentInitialStates[AgentLegalForm.PERSON],
        ...prev,
        format: AgentLegalForm.PERSON,
      }));
    } else if (newFormat === AgentLegalForm.COMPANY) {
      setFormData((prev) => ({
        ...agentInitialStates[AgentLegalForm.COMPANY],
        ...prev,
        format: AgentLegalForm.COMPANY,
      }));
    } else {
      setFormData((prev) => ({
        ...agentInitialStates[AgentLegalForm.ENTREPRENEUR],
        ...prev,
        format: AgentLegalForm.ENTREPRENEUR,
      }));
    }

    setIsOgrnipOverflow(false);
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    const isCheckbox = type === "checkbox";
    const newValue = isCheckbox
      ? (e.target as HTMLInputElement).checked
      : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (
      requiresContractDate &&
      !isTechnical &&
      !String(formData.contract_date ?? "").trim()
    ) {
      alert(t("createAgent.contract.date"));
      return;
    }

    if (formRef.current && !formRef.current.checkValidity()) {
      alert(t("createAgent.errors.requiredFields"));
      return;
    }

    if (hasInvalidOgrnip) {
      alert(t("createAgent.validation.ogrnipMaxLength"));
      return;
    }

    if (provider === Provider.ARMENIA && hasInvalidArmenianFields) {
      alert(t("createAgent.errors.armenianOnly"));
      return;
    }

    try {
      if (isDraftMode) {
        const saveResponse = await apiClient.post("/agent-drafts", {
          id: draft.id,
          payload: formData,
          source_agent_id: agent?.id ?? null,
        });

        if (saveResponse.data.status !== "ok") {
          alert(saveResponse.data?.error ?? t("createAgent.errors.saveDraft"));
          return;
        }

        const response = await apiClient.post(
          `/agent-drafts/${saveResponse.data.draft.id}/publish`,
        );

        if (response.data.status === "ok") {
          onSuccess();
        } else {
          alert(response.data?.error ?? t("createAgent.errors.publish"));
        }
      } else if (agent) {
        if (!isDirty) {
          alert(t("createAgent.errors.noChanges"));
          return;
        }

        const updatedFields: Partial<AgentFormState> = {};

        Object.keys(formData).forEach((key) => {
          const typedKey = key as keyof AgentFormState;
          const newValue = formData[typedKey];

          if (
            newValue !== initialFormDataRef.current[typedKey] &&
            newValue !== undefined
          ) {
            updatedFields[typedKey] = newValue as any;
          }
        });

        const updatesArray = Object.entries(updatedFields).map(
          ([field, value]) => ({ field, value }),
        );

        if (
          updatesArray.some(
            ({ field, value }) => field === "privileges" && value === "AGENT",
          ) &&
          !String(formData.user_id_tn ?? "").trim()
        ) {
          alert(t("createAgent.errors.tnIdRequiredForWorkingStatus"));
          return;
        }

        if (
          updatesArray.some(
            ({ field, value }) => field === "privileges" && value === "AGENT",
          ) &&
          !hasOpenAccount(formData.tn_client_status)
        ) {
          alert(t("createAgent.errors.accountNotOpened"));
          return;
        }

        const response = await apiClient.post(`/agents/update`, {
          id: agent.id,
          updates: updatesArray,
        });

        if (response.data.status === "ok") {
          onSuccess();
        } else {
          alert(response.data?.error ?? t("createAgent.errors.update"));
        }
      } else {
        const response = await apiClient.post("/agents", formData);

        if (response.data.status === "ok") {
          onSuccess();
        } else {
          alert(response.data?.error ?? t("createAgent.errors.create"));
        }
      }
    } catch (err) {
      const error = err as AxiosError<{ error: string }>;
      const message =
        error.response?.data?.error ?? t("createAgent.errors.save");
      alert(message);
    }
  };

  const handleSaveDraft = async () => {
    if (!canSaveDraft) {
      return;
    }

    try {
      setIsSavingDraft(true);

      const response = await apiClient.post("/agent-drafts", {
        id: draft?.id,
        payload: formData,
        source_agent_id: agent?.id ?? null,
      });

      if (response.data.status === "ok") {
        onSuccess();
        return;
      }

      alert(response.data?.error ?? t("createAgent.errors.saveDraft"));
    } catch (err) {
      const error = err as AxiosError<{ error: string }>;
      const message =
        error.response?.data?.error ?? t("createAgent.errors.saveDraft");
      alert(message);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleDeleteDraft = async () => {
    if (!draft?.id) {
      return;
    }

    if (!window.confirm(t("createAgent.formActions.deleteDraftConfirm"))) {
      return;
    }

    try {
      setIsDeletingDraft(true);

      const response = await apiClient.delete(`/agent-drafts/${draft.id}`);

      if (response.data.status === "ok") {
        onSuccess();
        return;
      }

      alert(response.data?.error ?? t("createAgent.errors.deleteDraft"));
    } catch (err) {
      const error = err as AxiosError<{ error: string }>;
      const message =
        error.response?.data?.error ?? t("createAgent.errors.deleteDraft");
      alert(message);
    } finally {
      setIsDeletingDraft(false);
    }
  };

  useEffect(() => {
    if (isDraftMode) {
      const newData = getSanitizedDraftPayload(draft);
      setFormData(newData);
      initialFormDataRef.current = newData;
      setIsTechnical(Boolean(newData.is_technical));
      setIsOgrnipOverflow(false);
    } else if (agent) {
      const newData = { ...initialBaseState, ...agent };
      setFormData(newData);
      initialFormDataRef.current = newData;
      setIsTechnical(Boolean(newData.is_technical));
      setIsOgrnipOverflow(false);
    } else {
      setFormData(initialBaseState);
      initialFormDataRef.current = initialBaseState;
      setIsTechnical(false);
      setIsOgrnipOverflow(false);
    }
  }, [agent, draft, isDraftMode]);

  useEffect(() => {
    if (isAgentEditMode) {
      if (formRef.current) {
        setIsFormValid(
          formRef.current.checkValidity() &&
            !hasInvalidArmenianFields &&
            !hasInvalidOgrnip,
        );
      }
      return;
    }

    const validationErrors: string[] = [];

    if (isTechnical) {
      const ok =
        !!String(formData.format ?? "").trim() &&
        !!String(formData.first_name ?? "").trim() &&
        !!String(formData.last_name ?? "").trim() &&
        !!String(formData.user_id_tn ?? "").trim() &&
        !!String(formData.email ?? "").trim();

      if (!String(formData.format ?? "").trim())
        validationErrors.push(t("createAgent.validation.legalFormRequired"));
      if (!String(formData.first_name ?? "").trim())
        validationErrors.push(t("createAgent.validation.firstNameRequired"));
      if (!String(formData.last_name ?? "").trim())
        validationErrors.push(t("createAgent.validation.lastNameRequired"));
      if (!String(formData.user_id_tn ?? "").trim())
        validationErrors.push(t("createAgent.validation.tnClientIdRequired"));
      if (!String(formData.email ?? "").trim())
        validationErrors.push(t("createAgent.validation.emailRequired"));
      if (hasInvalidArmenianFields)
        validationErrors.push(t("createAgent.validation.armenianInvalid"));

      const isValid = ok && !hasInvalidArmenianFields;
      setIsFormValid(isValid);

      if (!isValid && shouldLog()) {
        console.warn("CreateAgentForm validation errors", {
          mode: isDraftMode ? "draft" : "create",
          provider,
          isTechnical,
          errors: validationErrors,
        });
      }

      return;
    }

    const invalidFieldNames = getInvalidFormFieldNames(formRef.current);

    if (invalidFieldNames.length) {
      validationErrors.push(
        ...invalidFieldNames.map((name) =>
          t("createAgent.validation.invalidField", { name }),
        ),
      );
    }

    if (hasInvalidArmenianFields)
      validationErrors.push(t("createAgent.validation.armenianInvalid"));
    if (hasInvalidOgrnip)
      validationErrors.push(t("createAgent.validation.ogrnipMaxLength"));
    if (
      requiresContractDate &&
      !isTechnical &&
      !String(formData.contract_date ?? "").trim()
    ) {
      validationErrors.push(t("createAgent.contract.date"));
    }

    const isValid =
      !!formRef.current?.checkValidity() &&
      !hasInvalidArmenianFields &&
      !hasInvalidOgrnip &&
      (!requiresContractDate ||
        isTechnical ||
        !!String(formData.contract_date ?? "").trim());

    setIsFormValid(isValid);

    if (!isValid && shouldLog()) {
      console.warn("CreateAgentForm validation errors", {
        mode: isDraftMode ? "draft" : "create",
        provider,
        isTechnical,
        errors: validationErrors,
      });
    }
  }, [
    formData,
    hasInvalidArmenianFields,
    hasInvalidOgrnip,
    isAgentEditMode,
    isDraftMode,
    isTechnical,
    requiresContractDate,
    provider,
    tnError,
    t,
  ]);

  useEffect(() => {
    if (!formData.user_id_tn) {
      setTnClient(undefined);
      setTnError(false);
      setFormData((prev) => ({
        ...prev,
        tn_client_status: null,
      }));
      return;
    }

    const handler = setTimeout(() => {
      setDebouncedTnId(formData.user_id_tn);
    }, 600);

    return () => clearTimeout(handler);
  }, [formData.user_id_tn]);

  useEffect(() => {
    if (!debouncedTnId) return;

    const fetchClient = async () => {
      setLoadingTn(true);
      setTnError(false);

      try {
        const response = await apiClient.get(
          "/agents/check/tn?tn_id=" + debouncedTnId,
        );

        if (response.data.success && response.data.client?.id) {
          setTnClient(response.data.client);
          setFormData((prev) => ({
            ...prev,
            tn_client_status: response.data.client?.status ?? null,
          }));
          setTnError(false);
        } else {
          setTnClient(undefined);
          setFormData((prev) => ({
            ...prev,
            tn_client_status: null,
          }));
          setTnError(true);
        }
      } catch {
        setTnClient(undefined);
        setFormData((prev) => ({
          ...prev,
          tn_client_status: null,
        }));
        setTnError(true);
      } finally {
        setLoadingTn(false);
      }
    };

    fetchClient();
  }, [debouncedTnId]);

  return (
    <FormContainer>
      <ClosedAgentAlert formData={formData} />

      <TabContext value={tab}>
        <AgentTabs
          tab={tab}
          isCreate={isCreateMode || isDraftMode}
          onChange={handleTabChange}
        />

        <TabPanel value="default">
          <Box component="form" onSubmit={handleSubmit} ref={formRef}>
            <DefaultTabContent
              agent={agent}
              draft={draft}
              formData={formData}
              setFormData={setFormData}
              isOgrnipOverflow={isOgrnipOverflow}
              setIsOgrnipOverflow={setIsOgrnipOverflow}
              isCreateMode={isCreateMode}
              isDraftMode={isDraftMode}
              isTechnical={isTechnical}
              setIsTechnical={setIsTechnical}
              techOnly={techOnly}
              provider={provider}
              motivations={motivations}
              motivationsLoading={motivationsLoading}
              loadingTn={loadingTn}
              tnError={tnError}
              tnClient={tnClient}
              isFirstNameAmInvalid={isFirstNameAmInvalid}
              isLastNameAmInvalid={isLastNameAmInvalid}
              isPatronymicAmInvalid={isPatronymicAmInvalid}
              isFormValid={isFormValid}
              isDirty={isDirty}
              isSavingDraft={isSavingDraft}
              isDeletingDraft={isDeletingDraft}
              canSaveDraft={canSaveDraft}
              onFormatChange={handleFormatChange}
              onChange={handleChange}
              onSaveDraft={handleSaveDraft}
              onDeleteDraft={handleDeleteDraft}
              onClose={onClose}
              onSuccess={onSuccess}
            />
          </Box>
        </TabPanel>

        <TabPanel value="rekviz">
          {isAgentEditMode && (
            <Section>
              <RekvizSection agentId={agent.id} />
            </Section>
          )}
        </TabPanel>

        <TabPanel value="files">
          {isAgentEditMode && (
            <Section>
              <FilesSection agentId={agent.id} />
            </Section>
          )}
        </TabPanel>
      </TabContext>
    </FormContainer>
  );
};

export default CreateAgentForm;
